/**
 * Module pur : calculs Gaz (ATRD7 + CTA + Accise TICGN).
 * Coefficients officiels CRE/GRDF (01/07/2025 – 30/06/2026).
 */

// Coefficients ATRD7
export const ATRD = {
  abonnement: {
    T1: 54.72,
    T2: 186.12,
    T3: 1301.40,
    T4: 21705.72,
  },
  variable: {
    T1: 44.94,
    T2: 12.08,
    T3: 8.69,
    T4: 1.18,
  },
  capacite: {
    T4_low: 144.00,
    T4_high: 288.00,
  },
};

// Taux CTA Gaz (≈ 20,80% de la part fixe ATRD)
export const TAUX_CTA_GAZ = 0.208;

// Taux Accise Gaz (TICGN)
export const ACCISE_GAZ_TAUX_STANDARD = 16.37; // €/MWh
export const ACCISE_GAZ_TAUX_REDUIT = 3.09; // €/MWh

// Sections NAF "industrielles" éligibles au taux réduit gaz
// Source : Nomenclature INSEE NAF rev. 2 (https://www.insee.fr/fr/information/2406147)
//   - Section B : Industries extractives (codes 05.xx à 09.xx)
//   - Section C : Industrie manufacturière (codes 10.xx à 33.xx)
//   - Section D : Production / distribution électricité, gaz, vapeur (code 35.xx)
//   - Section E : Production / distribution d'eau, assainissement, gestion des déchets (codes 36.xx à 39.xx)
export const SECTIONS_NAF_INDUSTRIELLES = ['B', 'C', 'D', 'E'];

// Plage des 2 premiers chiffres correspondant aux sections industrielles ci-dessus.
// Tout code NAF dont la division (2 premiers chiffres) tombe dans cet intervalle
// inclusif est considéré industriel.
const NAF_INDUSTRIEL_DIVISION_MIN = 5;
const NAF_INDUSTRIEL_DIVISION_MAX = 39;

/**
 * Extrait la division NAF (2 premiers chiffres) à partir d'un code.
 * Accepte les formats : "25.62A", "25.62", "2562A", "B0501", " 25  ".
 * Retourne null si aucune division valide n'est trouvée.
 */
export function getNafDivision(code) {
  if (!code || typeof code !== 'string') return null;
  // On retire toute lettre/espace puis on prend les 2 premiers chiffres restants
  const digits = code.replace(/[^0-9]/g, '');
  if (digits.length < 2) return null;
  const division = parseInt(digits.slice(0, 2), 10);
  if (Number.isNaN(division)) return null;
  return division;
}

/**
 * Vérifie si un code NAF correspond à un secteur industriel éligible au taux
 * réduit accise gaz.
 *
 * Logique :
 *  1. Si l'utilisateur saisit directement une lettre de section (ex: "B", "C"),
 *     on vérifie l'appartenance à SECTIONS_NAF_INDUSTRIELLES (rétro-compat).
 *  2. Sinon on extrait la division (2 premiers chiffres) et on vérifie qu'elle
 *     tombe dans la plage [05, 39] = sections B+C+D+E.
 *
 * Exemples :
 *  - "25.62A"  → 25 ∈ [5, 39] → true  (Section C - Industrie manufacturière)
 *  - "10.71B"  → 10 ∈ [5, 39] → true  (Section C - Boulangerie industrielle)
 *  - "01.11Z"  → 01 ∉ [5, 39] → false (Section A - Agriculture)
 *  - "47.11D"  → 47 ∉ [5, 39] → false (Section G - Commerce)
 *  - "B"       → true (saisie de section directe)
 *  - ""        → false
 */
export function estNafIndustrielGaz(code) {
  if (!code || typeof code !== 'string') return false;
  const trimmed = code.trim();
  if (!trimmed) return false;

  // Cas 1 : saisie directe d'une lettre de section
  if (trimmed.length === 1 && /[A-Z]/i.test(trimmed)) {
    return SECTIONS_NAF_INDUSTRIELLES.includes(trimmed.toUpperCase());
  }

  // Cas 2 : code NAF complet → division dans la plage industrielle
  const division = getNafDivision(trimmed);
  if (division === null) return false;
  return division >= NAF_INDUSTRIEL_DIVISION_MIN && division <= NAF_INDUSTRIEL_DIVISION_MAX;
}

/**
 * Calcule ATRD + CTA pour un segment gaz donné.
 * @param {string} segment        T1 | T2 | T3 | T4
 * @param {number} consoMWh       Consommation annuelle en MWh
 * @param {number} capacite       Capacité journalière (T4 uniquement) en MWh/j
 * @param {string|null} niveauCapacite  T4_low | T4_high
 */
export function calculATRD(segment, consoMWh, capacite = 0, niveauCapacite = null) {
  const partFixe = ATRD.abonnement[segment] || 0;
  const partVariable = consoMWh * (ATRD.variable[segment] || 0);

  let partCapacite = 0;
  if (segment === 'T4' && capacite > 0 && niveauCapacite) {
    partCapacite = capacite * (ATRD.capacite[niveauCapacite] || 0);
  }

  const atrdHT = partFixe + partVariable + partCapacite;
  const cta = partFixe * TAUX_CTA_GAZ;
  const totalHT = atrdHT + cta;

  return {
    segment,
    partFixe,
    partVariable,
    partCapacite,
    atrdHT,
    cta,
    totalHT,
  };
}
